const GiftCart = {
  OWN_SOURCES: /^gift-cart-sync$|^free-gift-|^promo-gift-|^checkout-gift-/,

  isOwnSource(source) {
    return source && this.OWN_SOURCES.test(source);
  },

  parseConfig() {
    const el = document.getElementById('gift-config');
    if (!el) return { customerHasOrders: false };
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return { customerHasOrders: false };
    }
  },

  isCartPage() {
    return window.location.pathname === '/cart' || window.location.pathname.endsWith('/cart');
  },

  async fetchCart() {
    const res = await fetch('/cart.js');
    if (!res.ok) return null;
    return res.json();
  },

  async applyCartUpdates(updates) {
    if (!updates || Object.keys(updates).length === 0) return null;
    const res = await fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates }),
    });
    if (!res.ok) return null;
    return res.json();
  },

  async addToCart(variantId, quantity) {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: quantity || 1 }),
    });
    if (!res.ok) return null;
    return this.fetchCart();
  },

  publishCartUpdate(source, cartData) {
    if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined' && cartData) {
      publish(PUB_SUB_EVENTS.cartUpdate, { source, cartData });
    }
  },

  itemHasKachingBundleProperty(item) {
    if (!item?.properties) return false;
    if (Array.isArray(item.properties)) {
      return item.properties.some((p) => (p.name || p.first || p.title || '') === '__kaching_bundles');
    }
    return Object.keys(item.properties).includes('__kaching_bundles');
  },

  hasKachingBundleInCart(cart) {
    if (!cart?.items) return false;
    return cart.items.some((item) => this.itemHasKachingBundleProperty(item));
  },

  /** True when the first-order gift variant is already in cart via a Kaching bundle line. */
  hasKachingBundleFreeGiftInCart(cart, variantId) {
    if (!cart?.items || !variantId) return false;
    const vid = parseInt(variantId, 10);
    return cart.items.some(
      (item) =>
        parseInt(item.variant_id, 10) === vid && this.itemHasKachingBundleProperty(item)
    );
  },

  countEligibleQuantityInCart(cart, excludeVariantIds, eligibleProductIds, requireSellingPlan) {
    if (!cart?.items) return 0;
    const excludeSet = new Set(
      (Array.isArray(excludeVariantIds) ? excludeVariantIds : [excludeVariantIds])
        .map((id) => parseInt(id, 10))
        .filter((id) => !isNaN(id))
    );
    let total = 0;

    for (const item of cart.items) {
      if (excludeSet.has(parseInt(item.variant_id, 10))) continue;

      if (eligibleProductIds && eligibleProductIds.size > 0) {
        const productId = parseInt(item.product_id, 10);
        if (isNaN(productId) || !eligibleProductIds.has(productId)) continue;
      }

      if (requireSellingPlan && !item.selling_plan_allocation) continue;
      total += item.quantity;
    }

    return total;
  },

  parsePromoGiftRules(rules) {
    if (!Array.isArray(rules)) return [];
    return rules
      .map((rule) => ({
        minEligibleQty: parseInt(rule.minEligibleQty, 10),
        variantId: parseInt(rule.variantId, 10),
        giftQty: parseInt(rule.giftQty, 10),
      }))
      .filter(
        (rule) =>
          !isNaN(rule.minEligibleQty) &&
          rule.minEligibleQty > 0 &&
          !isNaN(rule.variantId) &&
          !isNaN(rule.giftQty) &&
          rule.giftQty >= 0
      );
  },

  resolvePromoGiftTargetQuantities(eligibleQty, rules, promoGiftVariantIds) {
    const targets = new Map();
    for (const vid of promoGiftVariantIds) {
      targets.set(parseInt(vid, 10), 0);
    }

    if (!rules.length) {
      if (eligibleQty > 0) {
        for (const vid of promoGiftVariantIds) {
          targets.set(parseInt(vid, 10), 1);
        }
      }
      return targets;
    }

    const sortedRules = [...rules].sort((a, b) => b.minEligibleQty - a.minEligibleQty);
    const assignedVariants = new Set();

    for (const rule of sortedRules) {
      if (assignedVariants.has(rule.variantId)) continue;
      if (eligibleQty >= rule.minEligibleQty) {
        targets.set(rule.variantId, rule.giftQty);
        assignedVariants.add(rule.variantId);
      }
    }

    return targets;
  },

  getPromoGiftQuantityUpdates(cart, promoGiftVariantIds, targetQuantities, allowExtraVariantIds) {
    const updates = {};
    const allowExtraSet = new Set(
      (allowExtraVariantIds || []).map((id) => parseInt(id, 10)).filter((id) => !isNaN(id))
    );

    for (const vid of promoGiftVariantIds) {
      const parsedVid = parseInt(vid, 10);
      const targetQty = targetQuantities.get(parsedVid) || 0;
      const currentQty = cart.items
        .filter((item) => parseInt(item.variant_id, 10) === parsedVid)
        .reduce((sum, item) => sum + item.quantity, 0);

      if (allowExtraSet.has(parsedVid)) {
        if (targetQty > 0 && currentQty < targetQty) {
          updates[parsedVid] = targetQty;
        }
        continue;
      }

      if (currentQty !== targetQty) {
        updates[parsedVid] = targetQty;
      }
    }

    return updates;
  },

  getVariantQty(cart, variantId) {
    const vid = parseInt(variantId, 10);
    return cart.items
      .filter((item) => parseInt(item.variant_id, 10) === vid)
      .reduce((sum, item) => sum + item.quantity, 0);
  },

  async refreshCartSections() {
    const mainCartItems = document.getElementById('main-cart-items');
    const cartUrl = window.routes?.cart_url || '/cart';

    if (!mainCartItems?.dataset?.id) {
      window.location.reload();
      return false;
    }

    const sectionId = mainCartItems.dataset.id;
    const sections = [sectionId, 'cart-icon-bubble', 'cart-live-region-text'];

    try {
      const res = await fetch(`${cartUrl}?sections=${sections.join(',')}`);
      if (!res.ok) throw new Error('section fetch failed');

      const data = await res.json();
      const sectionRenderers = [
        { id: 'main-cart-items', section: sectionId, selector: '.js-contents' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
        { id: 'cart-live-region-text', section: 'cart-live-region-text', selector: '.shopify-section' },
      ];

      for (const cfg of sectionRenderers) {
        const html = data[cfg.section];
        if (!html) continue;
        const root = document.getElementById(cfg.id);
        if (!root) continue;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const source = doc.querySelector(cfg.selector) || doc.body.firstElementChild;
        const target = root.querySelector(cfg.selector) || root;
        if (source && target) {
          target.innerHTML = source.innerHTML;
        }
      }

      const cartFooter = document.getElementById('main-cart-footer');
      const cartRes = await fetch('/cart.js');
      if (cartRes.ok) {
        const cart = await cartRes.json();
        mainCartItems.classList.toggle('is-empty', cart.item_count === 0);
        if (cartFooter) cartFooter.classList.toggle('is-empty', cart.item_count === 0);
      }

      return true;
    } catch (e) {
      window.location.reload();
      return false;
    }
  },
};

class GiftCollectionCache {
  constructor() {
    this.#cache = new Map();
    this.#pending = new Map();
  }

  #cache;
  #pending;

  async getProductIds(handle) {
    if (!handle) return null;
    if (this.#cache.has(handle)) return this.#cache.get(handle);
    if (this.#pending.has(handle)) return this.#pending.get(handle);

    const promise = this.#fetchAll(handle);
    this.#pending.set(handle, promise);

    try {
      const ids = await promise;
      this.#cache.set(handle, ids);
      return ids;
    } finally {
      this.#pending.delete(handle);
    }
  }

  mergeExtraIds(ids, extraIds) {
    const set = ids instanceof Set ? new Set(ids) : new Set();
    if (Array.isArray(extraIds)) {
      for (const id of extraIds) {
        const parsed = parseInt(id, 10);
        if (!isNaN(parsed)) set.add(parsed);
      }
    }
    return set.size > 0 ? set : null;
  }

  async #fetchAll(handle) {
    const ids = new Set();
    const pageSize = 50;
    let page = 1;

    while (page <= 20) {
      const res = await fetch(`/collections/${handle}/products.json?limit=${pageSize}&page=${page}`);
      if (!res.ok) break;

      const data = await res.json();
      const products = data.products || [];
      for (const product of products) {
        ids.add(product.id);
      }

      if (products.length < pageSize) break;
      page += 1;
    }

    return ids;
  }
}

window.GiftCart = GiftCart;
window.GiftCollectionCache = GiftCollectionCache;
